import { useState, useMemo, useCallback, useRef } from "react";
import type { BeforeCapture, DropResult } from "@hello-pangea/dnd";
import type { UseGrowerAssignmentProps, GrowerInfo, GrowerSortOption } from "./types";

const UNASSIGNED_DROPPABLE_ID = "unassigned";

export function useGrowerAssignment<TEntity extends { id: string }>(props: UseGrowerAssignmentProps<TEntity>) {
	const { jobs, entities, assignments, onAssign } = props;

	// Multi-select state
	const [selectedGrowers, setSelectedGrowers] = useState<Set<string>>(new Set());
	const lastClickedGrower = useRef<string | null>(null);
	const [draggingSelectedCount, setDraggingSelectedCount] = useState(0);
	const [growerSortOption, setGrowerSortOption] = useState<GrowerSortOption>("name-asc");

	const clearSelection = useCallback(() => {
		setSelectedGrowers(new Set());
		lastClickedGrower.current = null;
	}, []);

	// Group jobs by grower name to get grower info
	const growers = useMemo((): GrowerInfo[] => {
		const growerMap = new Map<string, number>();
		for (const job of jobs) {
			const name = job.importGrowerName || job.growerName || "";
			if (name) {
				growerMap.set(name, (growerMap.get(name) || 0) + 1);
			}
		}
		return Array.from(growerMap.entries())
			.map(([name, jobCount]) => ({ name, jobCount }))
			.sort((a, b) => a.name.localeCompare(b.name));
	}, [jobs]);

	// Unassigned growers (not in assignments), sorted by current sort option
	const unassignedGrowers = useMemo(() => {
		const filtered = growers.filter((g) => !assignments[g.name]);
		return filtered.sort((a, b) => {
			switch (growerSortOption) {
				case "name-asc":
					return a.name.localeCompare(b.name);
				case "name-desc":
					return b.name.localeCompare(a.name);
				case "count-desc":
					return b.jobCount - a.jobCount || a.name.localeCompare(b.name);
				case "count-asc":
					return a.jobCount - b.jobCount || a.name.localeCompare(b.name);
			}
		});
	}, [growers, assignments, growerSortOption]);

	// Handle click with optional shift for range selection
	const handleGrowerClick = useCallback(
		(name: string, shiftKey: boolean) => {
			if (shiftKey && lastClickedGrower.current) {
				const anchorIdx = unassignedGrowers.findIndex((g) => g.name === lastClickedGrower.current);
				const targetIdx = unassignedGrowers.findIndex((g) => g.name === name);
				if (anchorIdx !== -1 && targetIdx !== -1) {
					const start = Math.min(anchorIdx, targetIdx);
					const end = Math.max(anchorIdx, targetIdx);
					setSelectedGrowers((prev) => {
						const next = new Set(prev);
						for (let i = start; i <= end; i++) {
							next.add(unassignedGrowers[i].name);
						}
						return next;
					});
					lastClickedGrower.current = name;
					return;
				}
			}
			// Normal toggle
			setSelectedGrowers((prev) => {
				const next = new Set(prev);
				if (next.has(name)) {
					next.delete(name);
				} else {
					next.add(name);
				}
				return next;
			});
			lastClickedGrower.current = name;
		},
		[unassignedGrowers],
	);

	// Growers grouped by entity
	const growersByEntity = useMemo(() => {
		const map = new Map<string, GrowerInfo[]>();
		for (const entity of entities) {
			map.set(entity.id, []);
		}
		for (const grower of growers) {
			const entityId = assignments[grower.name];
			if (entityId && map.has(entityId)) {
				map.get(entityId)!.push(grower);
			}
		}
		return map;
	}, [growers, entities, assignments]);

	// Handle drag start — track multi-drag count
	const handleBeforeCapture = useCallback(
		(before: BeforeCapture) => {
			if (selectedGrowers.has(before.draggableId)) {
				setDraggingSelectedCount(selectedGrowers.size);
			} else {
				setDraggingSelectedCount(0);
			}
		},
		[selectedGrowers],
	);

	// Handle drag end
	const handleDragEnd = useCallback(
		(result: DropResult) => {
			const { draggableId, destination } = result;
			setDraggingSelectedCount(0);
			if (!destination) return;

			const growerName = draggableId;
			const targetId = destination.droppableId;
			const isMultiDrag = selectedGrowers.has(growerName) && selectedGrowers.size > 0;
			const growerNames = isMultiDrag ? [...selectedGrowers] : [growerName];

			const newAssignments = { ...assignments };

			for (const name of growerNames) {
				if (targetId === UNASSIGNED_DROPPABLE_ID) {
					delete newAssignments[name];
				} else {
					newAssignments[name] = targetId;
				}
			}

			onAssign(newAssignments);
			if (isMultiDrag) {
				setSelectedGrowers(new Set());
				lastClickedGrower.current = null;
			}
		},
		[assignments, onAssign, selectedGrowers],
	);

	// Bulk assignment handlers
	const assignAllToEntity = useCallback(
		(entityId: string) => {
			const newAssignments: Record<string, string> = {};
			for (const grower of growers) {
				newAssignments[grower.name] = entityId;
			}
			onAssign(newAssignments);
		},
		[growers, onAssign],
	);

	const assignUnassignedToEntity = useCallback(
		(entityId: string) => {
			const newAssignments = { ...assignments };
			for (const grower of unassignedGrowers) {
				newAssignments[grower.name] = entityId;
			}
			onAssign(newAssignments);
		},
		[assignments, unassignedGrowers, onAssign],
	);

	const unassignAllFromEntity = useCallback(
		(entityId: string) => {
			const newAssignments = { ...assignments };
			const assigned = growersByEntity.get(entityId) || [];
			for (const grower of assigned) {
				delete newAssignments[grower.name];
			}
			onAssign(newAssignments);
		},
		[assignments, growersByEntity, onAssign],
	);

	const assignSelectedToEntity = useCallback(
		(entityId: string) => {
			if (selectedGrowers.size === 0) return;
			const newAssignments = { ...assignments };
			for (const name of selectedGrowers) {
				newAssignments[name] = entityId;
			}
			onAssign(newAssignments);
			setSelectedGrowers(new Set());
		},
		[assignments, selectedGrowers, onAssign],
	);

	const unassignAll = useCallback(() => {
		onAssign({});
	}, [onAssign]);

	return {
		growers,
		unassignedGrowers,
		growersByEntity,
		selectedGrowers,
		handleGrowerClick,
		clearSelection,
		draggingSelectedCount,
		handleBeforeCapture,
		handleDragEnd,
		assignAllToEntity,
		assignUnassignedToEntity,
		unassignAllFromEntity,
		assignSelectedToEntity,
		unassignAll,
		growerSortOption,
		setGrowerSortOption,
		UNASSIGNED_DROPPABLE_ID,
	};
}
